import { useState, useEffect } from "react";
import { Button } from "@/projects/NPS/Stepper/common/button";
import { notify } from "@/components/common/Notification";
import EmailSendOtpService from "@/services/NPS/Stepper/APIs/emailSendOtpService";
import Loader from "@/projects/NPS/Stepper/common/Loader";

const OtpVerificationOfEmailAndMobile = ({
  onSubmit,
  registrationData,
  setSaveEmail,
}: {
  onClose: () => void;
  onSubmit: () => void;
  setSaveEmail: (email: string) => void;
  registrationData: {
    pan: string;
    fullName: string;
    dob: string;
    mobile: string;
    email: string;
    code_reference: string;
  } | null;
}) => {
  const [email, setEmail] = useState(""); // NEW FIELD
  const [emailOtp, setEmailOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false); // NEW FLAG
  const [timer, setTimer] = useState(90);
  const [codeReference, setCodeReference] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (registrationData?.email) {
      setEmail(registrationData.email);
    }
    if (registrationData?.code_reference) {
      setCodeReference(registrationData.code_reference);
    }
  }, [registrationData]);

  useEffect(() => {
    if (timer > 0 && otpSent) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    }
  }, [timer, otpSent]);

  const handleSendOtp = async () => {
    if (!email) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await EmailSendOtpService.emailSendOtp({
        mail: email,
      });
      if (response?.success?.code === "Success") {
        notify.success("OTP sent successfully!");
        setOtpSent(true);
        setTimer(90);
        setEmailError(null);
        setCodeReference(response.code_reference);
        setMessage("sent otp");
      } else {
        setEmailError(response?.message ?? "Failed to send OTP.");
      }
    } catch (err) {
      console.error(err);
      setEmailError("Something went wrong while sending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!emailOtp) {
      setEmailError("Please enter OTP.");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        mail: email,
        otp: emailOtp,
        code_reference: codeReference,
      };

      const response = await EmailSendOtpService.emailVerifyOtp(payload);

      if (response?.is_email_verified) {
        notify.success(response?.message ?? "OTP verified successfully!");
        localStorage.setItem("isEmail", email);
        localStorage.setItem("userInfo", JSON.stringify(response.user));
        setSaveEmail(email);
        setMessage("verifying otp");
        onSubmit();
      } else {
        setEmailError(response?.message ?? "OTP verification failed.");
        // notify.error(response?.message ?? "OTP verification failed.");
      }
    } catch (err) {
      console.error(err);
      notify.error("Failed to verify OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await EmailSendOtpService.emailResendOtp({
        code_reference: codeReference,
      });

      if (response?.success) {
        notify.success("OTP resent successfully!");
        setTimer(90);
      } else {
        console.error("Failed to resend OTP.");
      }
    } catch (err) {
      console.error(err);
      console.error("Error resending OTP.");
    }
  };

  const formattedTime = `${String(Math.floor(timer / 60)).padStart(
    2,
    "0"
  )}:${String(timer % 60).padStart(2, "0")}`;

  return (
    <div className="mt-5 w-full p-6 md:mt-0 md:p-8">
      {isLoading && <Loader message={message ?? "Verifying OTP..."} />}

      <form onSubmit={e => e.preventDefault()} className="space-y-4">
        {/* Error */}
        {emailError && (
          <p className="mb-4 font-nunito text-[16px] font-bold text-red-500">
            {emailError}
          </p>
        )}

        <h2 className="font-nunito text-[26px] font-bold text-[#266985]">
          Email Verification
        </h2>

        {/* Email Input */}
        <div>
          <label className="font-nunito font-semibold text-[#707070]">
            Email ID
          </label>
          <div className="flex-1">
            <input
              type="email"
              disabled={otpSent}
              placeholder="Enter your Email ID"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setEmailError(null);
              }}
              className={`w-full rounded-[6px] border bg-[#f5f5f5] px-4 py-2.5 font-nunito text-[16px] font-bold text-[#2F363F] placeholder-[#2F363F] outline-none ${otpSent ? "cursor-not-allowed bg-muted" : ""} ${
                emailError
                  ? "border-red-500 focus:border-red-500"
                  : "focus:border-[#1AAE90]"
              }`}
            />
            {/* {emailError && (
              <p className="mt-2 font-nunito text-[14px] font-semibold text-red-500">
                {emailError}
              </p>
            )} */}
          </div>
        </div>

        {/* Send OTP Button */}
        {!otpSent && (
          <Button
            onClick={handleSendOtp}
            className="h-[45px] w-full rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] font-nunito text-white"
          >
            Send OTP
          </Button>
        )}

        {/* OTP Section */}
        {otpSent && (
          <>
            <div className="flex flex-col">
              <label className="font-nunito font-semibold text-[#707070]">
                Enter OTP
              </label>
              <input
                type="text"
                maxLength={6}
                value={emailOtp}
                onChange={e => setEmailOtp(e.target.value)}
                placeholder="Enter OTP"
                className="mt-2 w-full rounded-md border border-[#DFE7EC] bg-[#f5f5f5] px-3 py-2.5 font-nunito"
              />
            </div>

            {/* Timer */}
            <p className="text-center font-nunito font-semibold">
              {timer > 0 ? (
                <>Resend in {formattedTime}</>
              ) : (
                <button
                  type="button"
                  className="text-[#1DAB91] underline"
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </button>
              )}
            </p>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              className="h-[50px] w-full rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] font-nunito text-white"
            >
              Verify OTP
            </Button>
          </>
        )}
      </form>
    </div>
  );
};

export default OtpVerificationOfEmailAndMobile;
